/**
 * Что раздел «Оплаты» спрашивает у `ops-api` — этап P3 (§2, §3 ТЗ портала).
 *
 * Адреса — `services/ops/api/routers/portal_payments.py`; правила за ними —
 * функции сервиса `ops`, те же, что у кнопок бота. Суммы — строками в
 * рублях с копейками; арифметики над деньгами в браузере нет.
 */

import { request } from '../../api';

export interface PayInvoice {
  id: number;
  number: string;
  external_number: string | null;
  supplier: { id: number; name: string };
  doc_date: string;
  is_posted: boolean;
  is_active: boolean;
  due_date: string | null;
  overdue_days: number;
  due_override: { date: string; reason: string | null } | null;
  total: string;
  vat: string | null;
  paid: string;
  adjusted: string;
  balance: string;
  status: string;
  is_settled: boolean;
  late_posting_days: number;
  backdated: boolean;
  source: string;
}

export interface Order {
  supplier_id: number;
  first_id: number;
  invoice_ids: number[];
  amount: string;
  due_date: string;
  pay_by: string;
  pay_by_weekday: string;
  overdue: boolean;
  invoices: PayInvoice[];
}

export type WeekView = 'all' | 'due' | 'late';

export interface Week {
  today: string;
  view?: WeekView;
  start: string;
  end: string;
  orders: number;
  total: string;
  synced_at: string | null;
  suppliers: {
    supplier_id: number;
    supplier_name: string;
    split: boolean;
    total: string;
    orders: Order[];
  }[];
}

export interface OrderPaid {
  message: string;
  paid: number;
  of: number;
  total: string;
  problems: string[];
}

export interface Debts {
  today: string;
  synced_at: string | null;
  threshold: string;
  overdue: { count: number; total: string };
  unposted: { count: number; total: string };
  debt: { count: number; total: string };
  suppliers: { id: number; name: string; count: number; total: string; overdue: number }[];
}

export type ListKind = 'overdue' | 'unposted' | 'supplier' | 'search';

export interface InvoiceList {
  title: string;
  today: string;
  synced_at: string | null;
  count: number;
  total: string;
  invoices: PayInvoice[];
}

export type CardAction = 'pay_full' | 'pay_partial' | 'change_due';

export interface PaymentMark {
  id: number;
  amount: string;
  paid_on: string;
  marked_by: string | null;
  marked_at: string;
  comment: string | null;
  cancelled: boolean;
  cancelled_at: string | null;
  cancel_reason: string | null;
  can_cancel: boolean;
}

export interface InvoiceCard {
  invoice: PayInvoice;
  threshold: string;
  needs_confirmation: boolean;
  actions: CardAction[];
  payments: PaymentMark[];
  adjustments: {
    id: number;
    kind: string;
    kind_title: string;
    amount: string;
    reason: string;
    created_at: string;
  }[];
}

export interface CardAnswer {
  message: string;
  card: InvoiceCard;
}

export interface ReportInfo {
  code: string;
  title: string;
  needs_period: boolean;
  supplier: boolean;
  min_total: boolean;
}

export interface ReportList {
  reports: ReportInfo[];
  suppliers: { id: number; name: string }[];
  default_days: number;
}

export interface ReportParams {
  from?: string;
  to?: string;
  supplier_id?: number;
  min_total?: string;
}

export interface Report {
  code: string;
  title: string;
  period: { from: string; to: string } | null;
  text: string;
  synced_at: string | null;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

const BASE = '/api/ops/payments';

/** Полями объекта, как `portalApi`: тест подменяет ровно один адрес. */
export const paymentsApi = {
  week: (view: WeekView = 'all', period: { start?: string; end?: string } = {}) =>
    request<Week>(`${BASE}/week${query({ view, ...period })}`),
  payOrder: (
    order: Pick<Order, 'supplier_id' | 'first_id' | 'invoice_ids'>,
    view: WeekView = 'all',
    period: { start?: string; end?: string } = {},
  ) =>
    request<OrderPaid>(`${BASE}/orders/paid`, {
      method: 'POST',
      body: {
        supplier_id: order.supplier_id,
        first_id: order.first_id,
        invoice_ids: order.invoice_ids,
        view,
        ...period,
      },
    }),
  payMany: (invoiceIds: number[]) =>
    request<OrderPaid>(`${BASE}/invoices/paid`, {
      method: 'POST',
      body: { invoice_ids: invoiceIds },
    }),
  debts: () => request<Debts>(`${BASE}/debts`),
  invoices: (kind: ListKind, extra: { supplier_id?: number; q?: string } = {}) =>
    request<InvoiceList>(`${BASE}/invoices${query({ list: kind, ...extra })}`),
  card: (id: number) => request<InvoiceCard>(`${BASE}/invoices/${id}`),
  pay: (id: number, body: { amount?: string; confirmed?: boolean }) =>
    request<CardAnswer>(`${BASE}/invoices/${id}/paid`, { method: 'POST', body }),
  changeDue: (id: number, body: { due_date: string; reason: string }) =>
    request<CardAnswer>(`${BASE}/invoices/${id}/due-date`, { method: 'POST', body }),
  cancelMark: (paymentId: number, reason: string) =>
    request<CardAnswer>(`${BASE}/marks/${paymentId}/cancel`, {
      method: 'POST',
      body: { reason },
    }),
  reports: () => request<ReportList>(`${BASE}/reports`),
  report: (code: string, params: ReportParams = {}) =>
    request<Report>(`${BASE}/reports/${encodeURIComponent(code)}${query({ ...params })}`),
};

/** Перечитывание: при возврате на вкладку и раз в 30 секунд (§3 ТЗ портала). */
export const LIVE = { refetchOnWindowFocus: true, refetchInterval: 30_000 } as const;
